/**
 * Horas e datas (§13.4.3, §10.1.4), funções puras. Toda hora é de Brasília, UTC−3 FIXO (sem horário de verão,
 * §10.1.1), derivada de `t_min` (minutos desde 00h00 de Brasília do dia da eleição) ou de um instante em ms por
 * aritmética em UTC. O fuso do aparelho nunca interfere: nenhum método de hora local de `Date`; o único
 * `Intl.DateTimeFormat` (dia da semana) leva `timeZone: 'America/Sao_Paulo'` (AC-10.1-03).
 *
 * Folha (FE §4.2): não importa `model/` nem `config`. Por isso a data da eleição padrão e `epochMs` moram aqui e
 * `src/model/time.ts` os reexporta; `ELECTION_DATE` é igual a `ELECTION.date` de src/config.ts (teste).
 */
import { DASH } from './numbers';

/** Dia da eleição (§1.2): `config.election_date` do site ao vivo. */
export const ELECTION_DATE = '2026-10-25';
/** Brasília = UTC−3 fixo. */
export const BRT_OFFSET_MIN = -180;

const MIN_MS = 60_000;
const DAY_MIN = 1440;
const DAY_S = 86_400;
const OFFSET_MS = BRT_OFFSET_MIN * MIN_MS;
const TIME_ZONE = 'America/Sao_Paulo';

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));
const mod = (a: number, n: number): number => ((a % n) + n) % n;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** [ano, mês 1–12, dia] de "AAAA-MM-DD" (aceita ISO completo: só a data conta); null se inválida. */
function ymd(date: string): [number, number, number] | null {
  const m = DATE_RE.exec(date);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return [y, mo, d];
}

/** Instante (ms) de 00h00 de Brasília do dia `electionDate` ("2026-10-25" → 2026-10-25T03:00:00Z). */
export function epochMs(electionDate: string = processDate): number {
  const p = ymd(electionDate) ?? (ymd(ELECTION_DATE) as [number, number, number]);
  return Date.UTC(p[0], p[1] - 1, p[2]) - OFFSET_MS;
}

// ── Data padrão do processo ─────────────────────────────────────────────────────────────────────────────────
// A data que vale quando a chamada não passa `electionDate`. No site ao vivo ninguém a configura e ela é
// `ELECTION_DATE`. A página do replay grava a data da noite aberta uma vez, antes do 1º render (uma noite por carga
// de página): sem isso, o `tMinOfIso` de um evento de 2022 cairia milhões de minutos antes do dia.
let processDate: string = ELECTION_DATE;
let processEpochMs = epochMs(ELECTION_DATE);

/** Troca a data padrão do processo ("AAAA-MM-DD"); data inválida é erro de quem chama. */
export function configureElectionDate(date: string): void {
  if (!ymd(date)) throw new RangeError(`data de eleição inválida: ${date}`);
  processDate = date.slice(0, 10);
  processEpochMs = epochMs(processDate);
}
/** A data padrão do processo: `ELECTION_DATE`, ou a da noite configurada. */
export const currentElectionDate = (): string => processDate;
/** 00h00 de Brasília da data padrão do processo, em ms. */
export const currentEpochMs = (): number => processEpochMs;

let weekdayFmt: Intl.DateTimeFormat | null = null;

/** Dia da semana abreviado ("dom.", "seg.") do dia `electionDate + days`, ao meio-dia de Brasília. */
function weekdayAt(electionDate: string, days: number): string {
  weekdayFmt ??= new Intl.DateTimeFormat('pt-BR', { weekday: 'short', timeZone: TIME_ZONE });
  return weekdayFmt.format(epochMs(electionDate) + days * DAY_MIN * MIN_MS + 12 * 60 * MIN_MS);
}

// ── t_min → texto ───────────────────────────────────────────────────────────────────────────────────────────

/** `pad2(floor(t/60) % 24)` + "h" + `pad2(floor(t) % 60)`: 545 → "09h05" · 1255 → "20h55" · 1491 → "00h51". */
export function fmtClock(tMin: number): string {
  if (!Number.isFinite(tMin)) return DASH;
  return `${pad2(mod(Math.floor(tMin / 60), 24))}h${pad2(mod(Math.floor(tMin), 60))}`;
}

/** Dia da semana de `election_date + floor(t/1440)` + ", " + fmtClock(t % 1440): 1491 → "seg., 00h51" (M15). */
export function fmtDayClock(tMin: number, electionDate: string = processDate): string {
  if (!Number.isFinite(tMin)) return DASH;
  return `${weekdayAt(electionDate, Math.floor(tMin / DAY_MIN))}, ${fmtClock(mod(tMin, DAY_MIN))}`;
}

/**
 * PADRÃO para qualquer hora de dado (status, eventos, pinos, toasts, replay, chips "às"): fmtClock no dia da eleição
 * (0 ≤ t < 1440), fmtDayClock fora dele (t ≥ 1440 é segunda, 26/10; M15): 1255 → "20h55" · 1491 → "seg., 00h51".
 */
export function fmtTime(tMin: number, electionDate: string = processDate): string {
  return tMin >= 0 && tMin < DAY_MIN ? fmtClock(tMin) : fmtDayClock(tMin, electionDate);
}

/** Hora curta de fechamento no exterior, sem zero à esquerda e sem ":00": 1140 → "19h" · 1170 → "19h30". */
export function fmtHourShort(tMin: number): string {
  if (!Number.isFinite(tMin)) return DASH;
  const h = mod(Math.floor(tMin / 60), 24);
  const m = mod(Math.floor(tMin), 60);
  return m === 0 ? `${h}h` : `${h}h${pad2(m)}`;
}

/** Marca da trilha (§10.1.4, sem dia): 17 → "17h" … 23 → "23h", 24 → "0h", 25 → "1h". */
export function fmtHourMark(h: number): string {
  if (!Number.isFinite(h)) return DASH;
  return `${mod(Math.floor(h), 24)}h`;
}

// ── instante (ms) → texto ───────────────────────────────────────────────────────────────────────────────────

/** Minutos desde 00h00 de Brasília de `electionDate` (o `t_min` de §10.1.1) de um instante em ms. */
export function tMinAt(ms: number, electionDate: string = processDate): number {
  return (ms - epochMs(electionDate)) / MIN_MS;
}

/** "HHhMM" de Brasília de um instante em ms, sem dia: 2026-10-25T23:55Z → "20h55". */
export function fmtClockAt(ms: number): string {
  if (!Number.isFinite(ms)) return DASH;
  return fmtClock((ms + OFFSET_MS) / MIN_MS);
}

/** fmtTime de um instante em ms (hora de dado, com dia fora de 25/10). */
export function fmtTimeAt(ms: number, electionDate: string = processDate): string {
  if (!Number.isFinite(ms)) return DASH;
  return fmtTime(tMinAt(ms, electionDate), electionDate);
}

/**
 * Relógio de parede de Brasília (`.timeline__clock` ao vivo, §10.2; horas do `title` do status): "21:03:37" ·
 * "9:03:37" (hora sem zero à esquerda). Se a data de Brasília não for `electionDate`, prefixo de dia de fmtDayClock:
 * "seg., 0:51:12" (§10.1.4).
 */
export function fmtClockSec(ms: number, electionDate: string = processDate): string {
  if (!Number.isFinite(ms)) return DASH;
  const s = mod(Math.floor((ms + OFFSET_MS) / 1000), DAY_S);
  const clock = `${Math.floor(s / 3600)}:${pad2(Math.floor((s % 3600) / 60))}:${pad2(s % 60)}`;
  const days = Math.floor((ms - epochMs(electionDate)) / (DAY_MIN * MIN_MS));
  return days === 0 ? clock : `${weekdayAt(electionDate, days)}, ${clock}`;
}

// ── durações e datas ────────────────────────────────────────────────────────────────────────────────────────

/**
 * Contagem regressiva até 17h00:00 (§10.8): `H:MM:SS` com hora > 0, senão `M:SS`. Segundos fracionários sobem
 * (16h59:59,5 ainda mostra "0:01"; "0:00" só com 0): 7629 → "2:07:09" · 299 → "4:59".
 */
export function fmtCountdown(seconds: number): string {
  if (!Number.isFinite(seconds)) return DASH;
  const s = Math.max(0, Math.ceil(seconds - 1e-6));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(sec)}` : `${m}:${pad2(sec)}`;
}

/** Dia sem zero + " de " + mês por extenso minúsculo: "2026-10-04" → "4 de outubro"; inválida → "–". */
export function fmtDateLong(isoDate: string): string {
  const p = ymd(isoDate);
  if (!p) return DASH;
  return `${p[2]} de ${MONTHS[p[1] - 1]}`;
}

/** [ano, mês 1–12, dia] do dia `isoDate + days`, por aritmética em UTC (o fuso do aparelho não entra); null se inválida. */
function ymdPlus(isoDate: string, days: number): [number, number, number] | null {
  const p = ymd(isoDate);
  if (!p || !Number.isFinite(days)) return null;
  const at = new Date(Date.UTC(p[0], p[1] - 1, p[2] + Math.floor(days)));
  return [at.getUTCFullYear(), at.getUTCMonth() + 1, at.getUTCDate()];
}

/**
 * Data curta COM ANO do dia `isoDate + days`: "2022-10-30" → "30/10/2022"; com `days = 1`, "31/10/2022" (a virada de
 * mês e de ano sai certa: "2022-10-31" + 1 → "01/11/2022"). Inválida → "–".
 */
export function fmtDateShort(isoDate: string, days = 0): string {
  const p = ymdPlus(isoDate, days);
  return p ? `${pad2(p[2])}/${pad2(p[1])}/${p[0]}` : DASH;
}

/**
 * Data curta com ano do instante `tMin` de uma noite (replay): o dia da eleição até 23h59 e o dia seguinte depois da
 * meia-noite, sempre de Brasília: (1152, "2022-10-30") → "30/10/2022" · (1458.5, "2022-10-30") → "31/10/2022".
 */
export function fmtNightDate(tMin: number, electionDate: string = processDate): string {
  if (!Number.isFinite(tMin)) return DASH;
  return fmtDateShort(electionDate, Math.floor(tMin / DAY_MIN));
}

/**
 * Marca de hora da barra de tempo de uma noite (replay): `fmtHourMark` no dia da eleição e, depois da meia-noite, com o
 * dia e o mês na frente: (23, "2022-10-30") → "23h" · (24, "2022-10-30") → "31/10 0h" · (25, …) → "31/10 1h".
 */
export function fmtNightHourMark(h: number, electionDate: string = processDate): string {
  if (!Number.isFinite(h)) return DASH;
  const days = Math.floor(h / 24);
  if (days === 0) return fmtHourMark(h);
  const p = ymdPlus(electionDate, days);
  return p ? `${pad2(p[2])}/${pad2(p[1])} ${fmtHourMark(h)}` : fmtHourMark(h);
}

// ── segundos do dia (caminho do voto público) ───────────────────────────────────────────────────────────────

/**
 * Hora de Brasília de "segundos desde 00h00 de Brasília" (o motor já somou o deslocamento da seção), com os segundos e a
 * hora sem zero à esquerda, como fmtClockSec: 28801 → "8:00:01" · 61200 → "17:00:00". Fora de 0–86399, o resto do dia.
 */
export function fmtSecOfDay(s: number): string {
  if (!Number.isFinite(s)) return DASH;
  const t = mod(Math.floor(s), DAY_S);
  return `${Math.floor(t / 3600)}:${pad2(Math.floor((t % 3600) / 60))}:${pad2(t % 60)}`;
}

/**
 * Duração em segundos, sem arredondar o que o dado traz em segundos inteiros: 54 → "54 s" · 114 → "1 min 54 s" ·
 * 3600 → "1 h" · 4016 → "1 h 6 min 56 s". Valor com fração (uma média) sai em segundos, com as casas do dado e vírgula
 * (228.5 → "228,5 s"): nada é arredondado. Negativo leva o sinal de menos tipográfico.
 */
export function fmtDurationS(s: number): string {
  if (!Number.isFinite(s)) return DASH;
  const sign = s < 0 ? '−' : '';
  const a = Math.abs(s);
  if (!Number.isInteger(a)) return `${sign}${String(a).replace('.', ',')} s`;
  const t = a;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h} h`);
  if (m > 0) parts.push(`${m} min`);
  if (sec > 0 || parts.length === 0) parts.push(`${sec} s`);
  return `${sign}${parts.join(' ')}`;
}
